/**
 * audioAutoFix — diagnose-and-heal audio processor for live broadcast.
 *
 * Why
 * ---
 * The AudioQualityMeter tells hosts *what's wrong*. Auto-Fix actually fixes
 * it in real-time so first-time creators don't need to know what a noise
 * gate or notch filter is. Three issues, three automatic responses:
 *
 *   • Clipping  → a hard limiter pulls peaks back to -1 dBFS (no audible
 *                  loss until you're really hot, then graceful ceiling).
 *   • Noise     → a soft noise gate driven by a live noise-floor estimate
 *                  cuts background hiss when the host isn't speaking.
 *   • Feedback  → a tracking notch BiquadFilter parks itself on the
 *                  loudest single FFT bin when feedback is detected, then
 *                  releases when the ringing stops.
 *
 * Design
 * ------
 * Returns a NEW MediaStream the caller swaps into the WebRTC sender (just
 * like Announcer FX). Internally:
 *
 *   src → notch → limiter → gateGain → dest
 *
 *  - notch:    BiquadFilter type=notch. freq set to detected feedback bin,
 *              Q=8 for narrow surgical cut. When idle it's parked at 18kHz
 *              (effectively bypassed for a voice signal).
 *  - limiter:  DynamicsCompressor with high ratio (12:1), threshold -3 dB,
 *              fast attack (3ms), short release (80ms). Acts as a peak
 *              ceiling rather than a tone-shaping comp.
 *  - gateGain: plain GainNode driven by a JS analyser loop. Below threshold
 *              gain ramps to 0 over 80ms (release); above threshold ramps to
 *              1 over 5ms (attack). The threshold is `noiseFloor + 6 dB`,
 *              auto-recalibrated every 5 seconds from a percentile ring.
 *
 * The chain is always wired identically; "off" means the gate/notch are
 * parked at neutral values and the limiter only catches genuine clips. So
 * enabling Auto-Fix is a single boolean toggle for the host.
 *
 * Mono-only contract: must be applied to a stream captured with
 * channelCount: 1 (see useMediaStream.js). Stereo capture disables AEC.
 */

let _ctx = null;
function ctx() {
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: "interactive" });
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

const RING_SIZE = 100;
const SAMPLE_INTERVAL_MS = 50;
const NOTCH_PARK_HZ = 18000;       // frequency that's effectively a bypass for voice
const NOTCH_ACTIVE_Q = 8;          // narrow surgical notch
const NOTCH_PARK_Q  = 0.5;         // wide and gentle when parked

function percentile(sortedArr, p) {
  if (sortedArr.length === 0) return -90;
  const idx = Math.max(0, Math.min(sortedArr.length - 1, Math.floor(p * sortedArr.length)));
  return sortedArr[idx];
}

/**
 * Apply Auto-Fix to a MediaStream.
 *
 * @param {MediaStream} sourceStream  raw mic stream (mono)
 * @returns {{
 *   stream: MediaStream,
 *   dispose: () => void,
 *   getStatus: () => { clipReducing: boolean, gateActive: boolean, notchHz: number|null }
 * }}
 */
export function applyAutoFix(sourceStream) {
  const ac = ctx();
  const src = ac.createMediaStreamSource(sourceStream);

  // 1. Notch — parked far above the voice band when not engaged.
  const notch = ac.createBiquadFilter();
  notch.type = "notch";
  notch.frequency.value = NOTCH_PARK_HZ;
  notch.Q.value = NOTCH_PARK_Q;

  // 2. Limiter — high-ratio comp acting as a peak ceiling.
  const limiter = ac.createDynamicsCompressor();
  limiter.threshold.value = -3;
  limiter.knee.value = 0;
  limiter.ratio.value = 12;
  limiter.attack.value = 0.003;
  limiter.release.value = 0.08;

  // 3. Soft noise gate — JS-driven GainNode. Starts at 1 (open).
  const gateGain = ac.createGain();
  gateGain.gain.value = 1;

  // 4. Destination → MediaStream out for WebRTC.
  const dest = ac.createMediaStreamDestination();

  // Analyser tap on the *input* — measures the raw mic so the gate fires
  // before the limiter, and so noise/feedback estimation isn't polluted by
  // our own processing.
  const analyser = ac.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.5;

  src.connect(analyser);
  src.connect(notch);
  notch.connect(limiter);
  limiter.connect(gateGain);
  gateGain.connect(dest);

  const timeBuf = new Float32Array(analyser.fftSize);
  const freqBuf = new Float32Array(analyser.frequencyBinCount);
  const ring = [];

  // Live status flags — exposed via getStatus() so the meter can show the
  // host *what* Auto-Fix is currently doing.
  const status = {
    clipReducing: false,
    gateActive: false,
    notchHz: null,
  };

  let feedbackStart = null;
  let lastClipFlash = 0;

  const sampleHandle = setInterval(() => {
    // RMS in dB
    analyser.getFloatTimeDomainData(timeBuf);
    let sum = 0;
    let peak = 0;
    for (let i = 0; i < timeBuf.length; i++) {
      const v = timeBuf[i];
      sum += v * v;
      const av = Math.abs(v);
      if (av > peak) peak = av;
    }
    const rms = Math.sqrt(sum / timeBuf.length);
    const db = rms > 0 ? Math.max(-90, 20 * Math.log10(rms)) : -90;

    ring.push(db);
    if (ring.length > RING_SIZE) ring.shift();

    // Noise floor — 10th percentile over rolling 5s window.
    const sorted = [...ring].sort((x, y) => x - y);
    const noiseDb = percentile(sorted, 0.1);

    // ----- Gate: open above (noise + 6 dB), close below (noise + 3 dB) -----
    // 3 dB hysteresis keeps the gate from chattering on breath / room tone.
    const openDb = noiseDb + 6;
    const closeDb = noiseDb + 3;
    const now = ac.currentTime;
    if (db >= openDb) {
      // Fast attack — open in 5 ms so consonants don't get clipped off
      gateGain.gain.cancelScheduledValues(now);
      gateGain.gain.setValueAtTime(gateGain.gain.value, now);
      gateGain.gain.linearRampToValueAtTime(1, now + 0.005);
      status.gateActive = false;
    } else if (db < closeDb) {
      // Slow release — fade to silence over 80 ms so it's natural-sounding
      gateGain.gain.cancelScheduledValues(now);
      gateGain.gain.setValueAtTime(gateGain.gain.value, now);
      gateGain.gain.linearRampToValueAtTime(0, now + 0.08);
      status.gateActive = true;
    }

    // ----- Clip status flash (the limiter is always working; we just
    // surface a UI hint when it's actually ducking peaks). -----
    if (peak >= 0.944) lastClipFlash = performance.now();
    status.clipReducing = (performance.now() - lastClipFlash) < 600;

    // ----- Feedback notch tracking -----
    analyser.getFloatFrequencyData(freqBuf);
    let mean = 0;
    let max = -Infinity;
    let maxIdx = -1;
    let validCount = 0;
    const start = 4;
    const end = Math.floor(freqBuf.length * 0.75);
    for (let i = start; i < end; i++) {
      const v = freqBuf[i];
      if (Number.isFinite(v) && v > -100) {
        mean += v;
        validCount++;
        if (v > max) { max = v; maxIdx = i; }
      }
    }
    mean = validCount > 0 ? mean / validCount : -90;
    const peakAboveMean = max - mean;

    if (peakAboveMean > 22 && max > -45) {
      if (feedbackStart == null) feedbackStart = performance.now();
      // Engage the notch as soon as we see the ring (no 500 ms wait — we
      // want to kill feedback the instant it starts).
      const hz = (maxIdx * ac.sampleRate) / analyser.fftSize;
      // Smoothly drift the notch frequency to the offending bin so a
      // wandering howl gets tracked rather than chasing it.
      notch.frequency.cancelScheduledValues(now);
      notch.frequency.linearRampToValueAtTime(hz, now + 0.05);
      notch.Q.cancelScheduledValues(now);
      notch.Q.linearRampToValueAtTime(NOTCH_ACTIVE_Q, now + 0.05);
      status.notchHz = Math.round(hz);
    } else {
      // No ring detected → release the notch back to its parking spot.
      // 800 ms hold prevents flapping on transient bright sounds (claps,
      // a sharp "S" consonant).
      if (feedbackStart != null && (performance.now() - feedbackStart) > 800) {
        feedbackStart = null;
        notch.frequency.cancelScheduledValues(now);
        notch.frequency.linearRampToValueAtTime(NOTCH_PARK_HZ, now + 0.2);
        notch.Q.cancelScheduledValues(now);
        notch.Q.linearRampToValueAtTime(NOTCH_PARK_Q, now + 0.2);
        status.notchHz = null;
      }
    }
  }, SAMPLE_INTERVAL_MS);

  return {
    stream: dest.stream,
    dispose: () => {
      clearInterval(sampleHandle);
      try { src.disconnect(); analyser.disconnect(); notch.disconnect(); limiter.disconnect(); gateGain.disconnect(); } catch {}
    },
    getStatus: () => ({ ...status }),
  };
}
